import { env } from "@/env";

import { isSegmentRewritable } from "./isSegmentRewritable";

const buildReverseMap = (locale: string): Record<string, string> => {
  const rewriteConfig = env.rewriteUrlsConfig[locale];
  const specificRewriteConfig = env.specificUrlsRewriteConfig[locale];

  const reverseMap: Record<string, string> = {};

  for (const [canonical, rewritten] of Object.entries(rewriteConfig ?? {})) {
    if (rewritten) {
      reverseMap[rewritten] = canonical;
    }
  }

  for (const [canonical, rewritten] of Object.entries(specificRewriteConfig ?? {})) {
    if (rewritten) {
      reverseMap[rewritten] = canonical;
    }
  }

  return reverseMap;
};

const removeLeadingSlash = (url: string): string => {
  return url.startsWith("/") ? url.slice(1) : url;
};

const sortRewritesByLengthDesc = (reverseMap: Record<string, string>): Array<[string, string]> => {
  return Object.entries(reverseMap).sort(([a], [b]) => b.length - a.length);
};

const tryFullPathMatch = (urlWithoutLeadingSlash: string, sortedRewrites: Array<[string, string]>): string | null => {
  for (const [rewritten, canonical] of sortedRewrites) {
    if (urlWithoutLeadingSlash === rewritten || urlWithoutLeadingSlash.startsWith(rewritten + "/")) {
      const remainder = urlWithoutLeadingSlash.slice(rewritten.length);
      return `/${canonical}${remainder}`;
    }
  }

  return null;
};

const tryFirstSegmentMatch = (url: string, reverseMap: Record<string, string>): string | null => {
  const urlParts = url.split("/");
  const firstSegment = urlParts[1];

  if (firstSegment) {
    const canonical = reverseMap[firstSegment];
    if (canonical && isSegmentRewritable(canonical)) {
      urlParts[1] = canonical;
      return urlParts.join("/");
    }
  }

  return null;
};

export const reverseRewriteUrls = (url: string, locale: string): string => {
  const reverseMap = buildReverseMap(locale);
  const urlWithoutLeadingSlash = removeLeadingSlash(url);
  const sortedRewrites = sortRewritesByLengthDesc(reverseMap);

  const fullPathMatch = tryFullPathMatch(urlWithoutLeadingSlash, sortedRewrites);
  if (fullPathMatch) {
    return fullPathMatch;
  }

  const firstSegmentMatch = tryFirstSegmentMatch(url, reverseMap);
  if (firstSegmentMatch) {
    return firstSegmentMatch;
  }

  return url;
};
